import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { WeatherAlert } from '@/hooks/useWeatherAlerts';

interface WeatherAlertBannerProps {
  openAlerts: WeatherAlert[];
  isLoading: boolean;
  onViewAlerts: () => void;
}

export function WeatherAlertBanner({ openAlerts, isLoading, onViewAlerts }: WeatherAlertBannerProps) {
  if (isLoading || openAlerts.length === 0) {
    return null;
  }

  const alertCount = openAlerts.length;
  const hasMultiple = alertCount > 1;

  const reasons = [...new Set(openAlerts.map(a => a.reason))];
  const primaryReason = reasons[0];

  const getReasonIcon = (reason: string) => {
    switch (reason) {
      case 'rain': return '🌧️';
      case 'snow': return '❄️';
      case 'cold': return '🌡️';
      case 'storm': return '⛈️';
      case 'heat': return '🔥';
      default: return '⚠️';
    }
  };

  return (
    <div 
      className={cn(
        "bg-warning/10 border border-warning/30 px-4 py-2 flex items-center justify-between gap-4",
        "animate-pulse-subtle"
      )}
    >
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1.5">
          <span className="text-lg">{getReasonIcon(primaryReason)}</span>
          <AlertTriangle className="h-4 w-4 text-warning" />
        </div>
        <div className="text-sm">
          <span className="font-semibold text-warning">Weather Alerts for Outdoor Work</span>
          <span className="text-muted-foreground ml-2">
            {alertCount} ticket{hasMultiple ? 's' : ''} scheduled in outdoor bay{hasMultiple ? 's' : ''} may be affected
          </span>
        </div>
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={onViewAlerts}
        className="border-warning/50 hover:bg-warning/10"
      >
        View Alerts
      </Button>
    </div>
  );
}
